// src/hooks/useWahaSending.tsx
// ============================================================================
// HOOK REUTILIZABLE DE ENVÍO POR WHATSAPP — re-cableado a lealify-base (Bloque C).
// ============================================================================
// Antes enviaba desde el NAVEGADOR (wahaService → proxy PHP) y guardaba el estado
// en Firestore (whatsappNumbers/{id}/campaigns), resolviendo sesión por sucursal
// (whatsappSessionService). Ahora todo es server-side y por la sesión ÚNICA del
// tenant (igual que campañas/cumpleaños):
//   - conexión:  whatsappApi.status() (GET /messages/whatsapp-status)
//   - modo:      whatsappApi.get/setSendMode() (auto/manual, tenant_settings)
//   - envío:     whatsappApi.sendManual() (POST /messages/send) → server-side, atómico
//   - estado:    whatsappApi.manualStatuses() (POST /messages/manual-status)
// El mensaje se RENDERIZA en el cliente (mismas variables @nombre/@marca/@puntos)
// y se manda ya resuelto. El modo MANUAL conserva el fallback a WhatsApp Web (wa.me).
// La API pública del hook NO cambia → UnsatisfiedClientsPage queda intacta.
//
// Se eliminó el multi-WhatsApp por sucursal (branchId): la base usa 1 sesión por
// tenant. SendTarget.branchId se ignora (compat de tipo).
// ============================================================================

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  Loader2, MessageSquare, Zap, Globe, CheckCircle, PhoneOff, AlertCircle,
} from 'lucide-react';
import CustomWhatsAppMessage from '../components/common/CustomWhatsAppMessage';
import type { MessageVariables } from '../components/common/CustomWhatsAppMessage';
import { canUseWhatsapp } from '../utils/whatsappAccess';
import { whatsappApi } from '../api/whatsappApi';

// ============================================================================
// TIPOS
// ============================================================================

export type SendStatus = 'sent' | 'no_whatsapp' | 'failed';

export type IdMode = 'yearly-client' | 'per-record' | 'per-campaign';

export interface WahaSendingConfig {
  campaignPrefix: string;
  docPrefix?: string;
  idMode: IdMode;
  storageKey: string;
  defaultMessage: string;
  messageOverride?: string;
  buttonLabels?: {
    action?: string;
    actionLong?: string;
    sent?: string;
  };
  onSendSuccess?: (target: SendTarget) => Promise<void>;
  onManualSendRegistered?: (target: SendTarget) => Promise<void>;
}

export interface SendTarget {
  id: string;
  clientId?: string;
  phoneNumber: string;
  countryCode?: string;
  variables: MessageVariables;
  /** Compat: la base usa 1 sesión por tenant, branchId se ignora (antes resolvía sesión). */
  branchId?: string;
  /** Imagen en base64 (data:image/...). Si se da, se envía como imagen+caption. */
  imageBase64?: string | null;
}

// ============================================================================
// HELPERS
// ============================================================================

// Año calculado al vuelo (NO constante de módulo): una pestaña abierta cruzando
// el 31-dic→1-ene usaría el año viejo en el id del estado de envío.
const currentYear = (): number => new Date().getFullYear();

const getDocId = (docPrefix: string, mode: IdMode, recordId: string): string => {
  if (mode === 'yearly-client') return `${docPrefix}_${currentYear()}_${recordId}`;
  return `${docPrefix}_${recordId}`;
};

const processMessageTemplate = (
  template: string,
  variables: MessageVariables,
  loyaltyMode: string = 'visits'
): string => {
  let msg = template;
  const nombreTrimmed = (variables.nombre || '').trim();
  const primerNombre = nombreTrimmed.split(' ')[0];

  msg = msg.replace(/@nombre/g, primerNombre || 'cliente');

  if (variables.marca) msg = msg.replace(/@marca/g, variables.marca);
  if (variables.puntos !== undefined) {
    // @puntos según modo: cashback => "$15.50", visitas => "15"
    const p = Number(variables.puntos) || 0;
    msg = msg.replace(/@puntos/g, loyaltyMode === 'cashback' ? `$${p.toFixed(2)}` : `${Math.floor(p)}`);
  }
  if (variables.ultimavisita) {
    const date = variables.ultimavisita instanceof Date ? variables.ultimavisita : new Date(variables.ultimavisita);
    msg = msg.replace(/@ultimavisita/g, date.toLocaleDateString('es-MX'));
  }

  return msg;
};

const normalizePhone = (countryCode: string, phoneNumber: string): string => {
  const cleanCC = (countryCode || '52').replace(/\+/g, '').replace(/\D/g, '');
  const cleanPhone = phoneNumber.replace(/\D/g, '');
  let full = cleanCC + cleanPhone;
  if (full.length === 12 && full.startsWith('52') && !full.startsWith('521')) {
    full = '521' + full.substring(2);
  }
  return full;
};

// ============================================================================
// HOOK
// ============================================================================

export const useWahaSending = (config: WahaSendingConfig) => {
  const { currentUser } = useAuth();
  const {
    campaignPrefix,
    idMode,
    storageKey,
    defaultMessage,
    messageOverride,
    buttonLabels,
    onSendSuccess,
    onManualSendRegistered,
  } = config;

  const resolvedDocPrefix = config.docPrefix || campaignPrefix;

  const labels = useMemo(() => ({
    action: buttonLabels?.action || 'Enviar',
    actionLong: buttonLabels?.actionLong || 'Enviar por WhatsApp',
    sent: buttonLabels?.sent || 'Enviado',
  }), [buttonLabels?.action, buttonLabels?.actionLong, buttonLabels?.sent]);

  const hasPremiumAccess = canUseWhatsapp(currentUser); // = PRO + WhatsApp provisionado (bajo solicitud)

  const [wahaConnected, setWahaConnected] = useState(false);
  const [isCheckingWaha, setIsCheckingWaha] = useState(false);
  const [whatsappSendMode, setWhatsappSendMode] = useState<'auto' | 'manual'>('auto');
  const [savingMode, setSavingMode] = useState(false);
  const [sendingId, setSendingId] = useState<string | null>(null);
  const [directSendResult, setDirectSendResult] = useState<{
    id: string; success: boolean; message: string;
  } | null>(null);
  const [sendStatuses, setSendStatuses] = useState<Map<string, SendStatus>>(new Map());
  const [loadingStatuses, setLoadingStatuses] = useState(false);

  const effectiveWaha = hasPremiumAccess && wahaConnected && whatsappSendMode === 'auto';

  const messageType = useMemo(() => {
    switch (storageKey) {
      case 'birthdayMessageTemplate': return 'birthday';
      case 'feedbackRecoveryMessageTemplate': return 'feedbackRecovery';
      default: return storageKey;
    }
  }, [storageKey]);

  const messageTemplate = messageOverride || currentUser?.customMessages?.[messageType] || defaultMessage;

  // ============================================================================
  // EFFECT: Estado de conexión WhatsApp (solo con acceso) — sesión del tenant.
  // ============================================================================
  useEffect(() => {
    if (!hasPremiumAccess) { setWahaConnected(false); return; }

    let cancelled = false;
    const checkWaha = async () => {
      setIsCheckingWaha(true);
      try {
        const result = await whatsappApi.status();
        if (!cancelled) setWahaConnected(!!result.connected);
      } catch (err) {
        console.error('useWahaSending: Error checking WAHA:', err);
        if (!cancelled) setWahaConnected(false);
      } finally {
        if (!cancelled) setIsCheckingWaha(false);
      }
    };

    checkWaha();
    return () => { cancelled = true; };
  }, [hasPremiumAccess]);

  // ============================================================================
  // EFFECT: Modo de envío auto/manual (tenant_settings)
  // ============================================================================
  useEffect(() => {
    if (!hasPremiumAccess) return;

    let cancelled = false;
    const loadMode = async () => {
      try {
        const { mode } = await whatsappApi.getSendMode();
        if (!cancelled && (mode === 'manual' || mode === 'auto')) setWhatsappSendMode(mode);
      } catch (err) {
        console.error('useWahaSending: Error loading send mode:', err);
      }
    };

    loadMode();
    return () => { cancelled = true; };
  }, [hasPremiumAccess]);

  // ============================================================================
  // Toggle send mode
  // ============================================================================
  const toggleWhatsappSendMode = useCallback(async () => {
    if (savingMode) return;

    const newMode = whatsappSendMode === 'auto' ? 'manual' : 'auto';
    setSavingMode(true);

    try {
      await whatsappApi.setSendMode(newMode);
      setWhatsappSendMode(newMode);
    } catch (err) {
      console.error('useWahaSending: Error toggling mode:', err);
    } finally {
      setSavingMode(false);
    }
  }, [whatsappSendMode, savingMode]);

  // ============================================================================
  // Actualiza solo el mapa local de estados (la persistencia la hace el servidor
  // dentro de sendManual; esto refleja el resultado en la UI sin re-consultar).
  // ============================================================================
  const setLocalStatus = useCallback((recordId: string, status: SendStatus) => {
    setSendStatuses(prev => {
      const next = new Map(prev);
      next.set(recordId, status);
      return next;
    });
  }, []);

  // Compat de API pública: antes persistía en Firestore; hoy solo refleja en local.
  const saveSendResult = useCallback(async (target: SendTarget, status: SendStatus) => {
    setLocalStatus(target.id, status);
  }, [setLocalStatus]);

  // ============================================================================
  // Cargar estados persistidos (lote) desde la base
  // ============================================================================
  const loadStatuses = useCallback(async (
    records: Array<{ id: string; clientId?: string }>
  ) => {
    if (!records.length) return;

    setLoadingStatuses(true);
    try {
      const refByRecord = new Map<string, string>();
      const refs: string[] = [];
      for (const r of records) {
        const ref = getDocId(resolvedDocPrefix, idMode, r.id);
        refByRecord.set(r.id, ref);
        refs.push(ref);
      }
      const { statuses } = await whatsappApi.manualStatuses(refs);
      const statusMap = new Map<string, SendStatus>();
      for (const r of records) {
        const st = statuses[refByRecord.get(r.id)!];
        if (st === 'sent' || st === 'no_whatsapp' || st === 'failed') statusMap.set(r.id, st);
      }
      setSendStatuses(statusMap);
    } catch (err) {
      console.error('useWahaSending: Error loading statuses:', err);
    } finally {
      setLoadingStatuses(false);
    }
  }, [resolvedDocPrefix, idMode]);

  // ============================================================================
  // Envío directo (server-side) vía la sesión del tenant
  // ============================================================================
  const handleDirectSend = useCallback(async (target: SendTarget) => {
    if (!target.phoneNumber || sendingId) return;

    setSendingId(target.id);
    setDirectSendResult(null);

    try {
      const mergedVars = {
        ...target.variables,
        marca: target.variables.marca || currentUser?.businessName,
      };
      const message = processMessageTemplate(messageTemplate, mergedVars, currentUser?.loyaltyMode || 'visits');
      const fullPhone = normalizePhone(target.countryCode || '52', target.phoneNumber);
      const refKey = getDocId(resolvedDocPrefix, idMode, target.id);

      const result = await whatsappApi.sendManual({
        ref_key: refKey,
        phone: fullPhone,
        message,
        image: target.imageBase64 || null,
        contact_id: target.clientId || target.id,
      });

      if (result.status === 'sent') {
        setDirectSendResult({ id: target.id, success: true, message: '¡Enviado! ⚡' });
        setLocalStatus(target.id, 'sent');
        if (onSendSuccess) {
          try { await onSendSuccess(target); }
          catch (cbErr) { console.error('useWahaSending: Error in onSendSuccess callback:', cbErr); }
        }
      } else if (result.status === 'no_whatsapp') {
        setDirectSendResult({ id: target.id, success: false, message: 'Sin WhatsApp' });
        setLocalStatus(target.id, 'no_whatsapp');
      } else {
        setDirectSendResult({ id: target.id, success: false, message: 'Error al enviar' });
        setLocalStatus(target.id, 'failed');
      }
    } catch (err: any) {
      console.error('useWahaSending: Error en handleDirectSend:', err);
      setDirectSendResult({ id: target.id, success: false, message: err?.message || 'Error al enviar' });
      setLocalStatus(target.id, 'failed');
    } finally {
      setSendingId(null);
      setTimeout(() => setDirectSendResult(null), 4000);
    }
  }, [currentUser?.businessName, currentUser?.loyaltyMode, messageTemplate, sendingId, resolvedDocPrefix, idMode, setLocalStatus, onSendSuccess]);

  // ============================================================================
  // Registrar envío manual (modo wa.me): el dueño ya envió desde su teléfono →
  // dejamos constancia 'sent' en el servidor sin enviar.
  // ============================================================================
  const registerManualSend = useCallback(async (target: SendTarget) => {
    try {
      const refKey = getDocId(resolvedDocPrefix, idMode, target.id);
      await whatsappApi.sendManual({ ref_key: refKey, register: true, contact_id: target.clientId || target.id });
      setLocalStatus(target.id, 'sent');
    } catch (err) {
      console.error('useWahaSending: Error registrando envío manual:', err);
    }

    if (onManualSendRegistered) {
      try { await onManualSendRegistered(target); }
      catch (cbErr) { console.error('useWahaSending: Error in onManualSendRegistered callback:', cbErr); }
    }
  }, [resolvedDocPrefix, idMode, setLocalStatus, onManualSendRegistered]);

  // ============================================================================
  // RENDER: Persisted badge
  // ============================================================================
  const renderPersistedBadge = useCallback((recordId: string) => {
    const status = sendStatuses.get(recordId);
    if (!status) return null;

    if (status === 'sent') {
      return (
        <span className="inline-flex items-center px-3 py-1.5 bg-green-50 dark:bg-green-500/10 text-green-700 dark:text-green-300 text-xs font-medium rounded-md border border-green-200">
          <CheckCircle className="h-3.5 w-3.5 mr-1" />
          {labels.sent}
        </span>
      );
    }

    if (status === 'no_whatsapp') {
      return (
        <span className="inline-flex items-center px-3 py-1.5 bg-yellow-50 dark:bg-yellow-500/10 text-yellow-700 dark:text-yellow-300 text-xs font-medium rounded-md border border-yellow-200">
          <PhoneOff className="h-3.5 w-3.5 mr-1" />
          Sin WhatsApp
        </span>
      );
    }

    return null;
  }, [sendStatuses, labels.sent]);

  // ============================================================================
  // RENDER: Send button
  // ============================================================================
  const renderSendButton = useCallback((target: SendTarget, size: 'sm' | 'md' = 'md') => {
    const isSending = sendingId === target.id;
    const tempResult = directSendResult?.id === target.id ? directSendResult : null;
    const persistedStatus = sendStatuses.get(target.id);

    if (persistedStatus === 'sent' || persistedStatus === 'no_whatsapp') {
      const badge = renderPersistedBadge(target.id);
      if (badge) return badge;
    }

    if (tempResult) {
      return (
        <span className={`inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md border ${
          tempResult.success
            ? 'bg-green-50 dark:bg-green-500/10 text-green-700 dark:text-green-300 border-green-200'
            : 'bg-red-50 dark:bg-red-500/10 text-red-700 dark:text-red-300 border-red-200'
        }`}>
          {tempResult.success ? (
            <CheckCircle className="h-3.5 w-3.5 mr-1" />
          ) : (
            <AlertCircle className="h-3.5 w-3.5 mr-1" />
          )}
          {tempResult.message}
        </span>
      );
    }

    if (persistedStatus === 'failed' && effectiveWaha) {
      return (
        <button
          onClick={(e) => { e.stopPropagation(); handleDirectSend(target); }}
          disabled={isSending || !!sendingId}
          className={`inline-flex items-center justify-center border border-red-300 font-medium rounded-md text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-500/10 hover:bg-red-100 dark:hover:bg-red-500/20 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed shadow-sm transition-colors ${
            size === 'sm' ? 'px-3 py-1.5 text-xs' : 'px-3 py-1.5 text-sm'
          }`}
        >
          {isSending ? (
            <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
          ) : (
            <AlertCircle className="h-3.5 w-3.5 mr-1" />
          )}
          Error - Reintentar
        </button>
      );
    }

    if (effectiveWaha && target.phoneNumber) {
      return (
        <button
          onClick={(e) => { e.stopPropagation(); handleDirectSend(target); }}
          disabled={isSending || !!sendingId}
          className={`inline-flex items-center justify-center border border-transparent font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed shadow-sm transition-colors ${
            size === 'sm' ? 'px-3 py-1.5 text-xs' : 'px-3 py-1.5 text-sm'
          }`}
        >
          {isSending ? (
            <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
          ) : (
            <Zap className="h-3.5 w-3.5 mr-1" />
          )}
          {size === 'sm' ? labels.action : labels.actionLong}
        </button>
      );
    }

    return (
      <CustomWhatsAppMessage
        countryCode={target.countryCode || '52'}
        phoneNumber={target.phoneNumber}
        variables={{
          nombre: target.variables.nombre,
          marca: target.variables.marca || currentUser?.businessName,
          puntos: target.variables.puntos,
          ultimavisita: target.variables.ultimavisita,
        }}
        buttonClassName={size === 'sm' ? 'text-xs' : ''}
        buttonLabel={labels.action}
        buttonIcon={<MessageSquare className={size === 'sm' ? 'h-3.5 w-3.5 mr-1' : 'h-4 w-4 mr-1.5'} />}
        hideEditButton={true}
        storageKey={storageKey}
        defaultMessage={defaultMessage}
      />
    );
  }, [
    sendingId, directSendResult, sendStatuses, effectiveWaha,
    handleDirectSend, renderPersistedBadge, labels, storageKey, defaultMessage,
    currentUser?.businessName,
  ]);

  // ============================================================================
  // RENDER: Mode toggle
  // ============================================================================
  const renderModeToggle = useCallback(() => {
    if (!hasPremiumAccess) return null;

    return (
      <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium cursor-pointer select-none transition-colors ${
        isCheckingWaha
          ? 'bg-canvas text-content-3'
          : wahaConnected && whatsappSendMode === 'auto'
            ? 'bg-green-100 dark:bg-green-500/20 text-green-800 dark:text-green-300'
            : 'bg-yellow-100 dark:bg-yellow-500/20 text-yellow-800 dark:text-yellow-300'
      }`}
        onClick={!isCheckingWaha && !savingMode ? toggleWhatsappSendMode : undefined}
        title={
          isCheckingWaha
            ? 'Verificando conexión...'
            : wahaConnected && whatsappSendMode === 'auto'
              ? 'Modo automático — clic para cambiar a manual'
              : wahaConnected && whatsappSendMode === 'manual'
                ? 'Modo manual — clic para cambiar a automático'
                : 'WAHA no conectado'
        }
      >
        {isCheckingWaha ? (
          <Loader2 className="h-3 w-3 animate-spin" />
        ) : savingMode ? (
          <Loader2 className="h-3 w-3 animate-spin" />
        ) : wahaConnected && whatsappSendMode === 'auto' ? (
          <Zap className="h-3 w-3" />
        ) : (
          <Globe className="h-3 w-3" />
        )}
        {isCheckingWaha
          ? 'Verificando...'
          : savingMode
            ? 'Guardando...'
            : wahaConnected && whatsappSendMode === 'auto'
              ? 'Envío directo'
              : wahaConnected && whatsappSendMode === 'manual'
                ? 'WhatsApp Web'
                : 'Sin WAHA'
        }
      </div>
    );
  }, [hasPremiumAccess, isCheckingWaha, wahaConnected, whatsappSendMode, savingMode, toggleWhatsappSendMode]);

  // ============================================================================
  // Return
  // ============================================================================
  return {
    wahaConnected,
    isCheckingWaha,
    whatsappSendMode,
    effectiveWaha,
    hasPremiumAccess,
    sendingId,
    directSendResult,
    sendStatuses,
    loadingStatuses,

    toggleWhatsappSendMode,
    handleDirectSend,
    loadStatuses,
    saveSendResult,
    registerManualSend,

    renderSendButton,
    renderModeToggle,
    renderPersistedBadge,
  };
};
